import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Check, RotateCcw, Loader2, Image as ImageIcon, ChevronDown, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as outwardDocApi from '@/services/outwardDocumentationApi';
import { listWarehouses, getOutwardWarehouseSummary } from '@/services/warehousesApi';

const OutwardDocumentation = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [jobWorkers, setJobWorkers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [extractingIds, setExtractingIds] = useState(new Set());
  const [editingData, setEditingData] = useState({});
  const [selectedVendors, setSelectedVendors] = useState({});
  const [vendorPopoverOpen, setVendorPopoverOpen] = useState({});
  const [viewingImages, setViewingImages] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [editModalEntry, setEditModalEntry] = useState(null);
  const [moveToDraftEntry, setMoveToDraftEntry] = useState(null);
  const [isMovingToDraft, setIsMovingToDraft] = useState(false);
  // 2026-06 — Warehouse filter + FY bifurcation (Phase 2).
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [warehouseSummary, setWarehouseSummary] = useState([]);

  // Format number in Indian format (1,23,456.78)
  const formatIndianNumber = (value) => {
    if (!value) return '';
    const num = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(num)) return value;
    return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  // Parse and format vendor doc date to DD/MM/YYYY
  const formatVendorDate = (dateStr) => {
    if (!dateStr) return '';
    
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      return dateStr;
    }
    
    try {
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) {
        const day = String(parsed.getDate()).padStart(2, '0');
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const year = parsed.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      // If parsing fails, return original
    }
    
    return dateStr;
  };

  // Format date and time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return { date: '-', time: '-' };
    
    let dateStr, timeStr;
    
    if (timestamp.includes('T')) {
      const dt = new Date(timestamp);
      dateStr = dt.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      timeStr = dt.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } else {
      const [datePart, timePart] = timestamp.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hour, minute] = timePart.split(':');
      dateStr = `${day}/${month}/${year}`;
      timeStr = `${hour}:${minute}`;
    }
    
    return { date: dateStr, time: timeStr };
  };

  // Get transfer type display label
  const getTransferTypeLabel = (type) => {
    const labels = {
      'job_worker': 'Job Worker',
      'internal_transfer': 'Internal Transfer',
      'customer': 'Customer Dispatch'
    };
    return labels[type] || type || '-';
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseFilter]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [entriesData, jobWorkersData, whData, sumData] = await Promise.all([
        outwardDocApi.getOutwardDocumentationEntries(warehouseFilter || null),
        outwardDocApi.getJobWorkers().catch(() => ({ job_workers: [] })),
        listWarehouses(false).catch(() => ({ warehouses: [] })),
        getOutwardWarehouseSummary().catch(() => ({ summary: [] })),
      ]);
      
      setEntries(entriesData.entries || []);
      setJobWorkers(jobWorkersData.job_workers || []);
      setWarehouses(whData.warehouses || []);
      setWarehouseSummary(sumData.summary || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load outward documentation entries');
    } finally {
      setIsLoading(false);
    }
  };

  const autoExtract = async (entryId) => {
    setExtractingIds(prev => new Set(prev).add(entryId));
    try {
      const result = await outwardDocApi.extractOutwardDocumentation(entryId);
      
      setEntries(prev => prev.map(e => 
        e.entry_id === entryId ? { ...e, documentation: result } : e
      ));
      
      toast.success(`Extracted data for ${result.document_barcode}`);
    } catch (error) {
      console.error('Error extracting:', error);
      toast.error(`Extraction failed for entry ${entryId}`);
    } finally {
      setExtractingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(entryId);
        return newSet;
      });
    }
  };

  const handleFieldChange = (entryId, field, value) => {
    setEditingData(prev => ({
      ...prev,
      [entryId]: {
        ...prev[entryId],
        [field]: value
      }
    }));
  };

  const handleVendorSelect = (entryId, vendor) => {
    setSelectedVendors(prev => ({
      ...prev,
      [entryId]: vendor
    }));
    handleFieldChange(entryId, 'vendor_name', vendor);
    setVendorPopoverOpen(prev => ({
      ...prev,
      [entryId]: false
    }));
  };

  const handleEditModal = (entry) => {
    setEditModalEntry(entry);
    if (!editingData[entry.entry_id]) {
      setEditingData(prev => ({
        ...prev,
        [entry.entry_id]: {
          vendor_name: entry.documentation?.vendor_name || '',
          vendor_doc_no: entry.documentation?.vendor_doc_no || '',
          vendor_doc_date: entry.documentation?.vendor_doc_date || '',
          qty: entry.documentation?.qty || '',
          value: entry.documentation?.value || '',
          vehicle_no: entry.documentation?.vehicle_no || '',
          destination: entry.documentation?.destination || entry.destination || '',
          remarks: entry.documentation?.remarks || ''
        }
      }));
    }
  };

  const handleSaveFromModal = () => {
    setEditModalEntry(null);
  };

  const handleViewImages = async (entryId) => {
    try {
      const response = await apiClient.post(`/outward/entries/${entryId}/signed-urls`);
      setViewingImages(response.data.signed_urls || []);
    } catch (error) {
      console.error('Error fetching image URLs:', error);
      toast.error('Failed to load images');
    }
  };

  const handleConfirm = async (entry) => {
    const vendorName = selectedVendors[entry.entry_id] || getFieldValue(entry, 'vendor_name');
    const vendorDocNo = getFieldValue(entry, 'vendor_doc_no');
    const vendorDocDate = getFieldValue(entry, 'vendor_doc_date');
    const qty = getFieldValue(entry, 'qty');

    if (!vendorName || !vendorDocNo || !vendorDocDate || !qty) {
      toast.error('Please fill in all mandatory fields: Party Name, Doc No, Doc Date, and Qty');
      return;
    }

    try {
      const updateData = { ...editingData[entry.entry_id] };
      
      if (selectedVendors[entry.entry_id]) {
        updateData.vendor_name = selectedVendors[entry.entry_id];
      }
      
      if (Object.keys(updateData).length > 0) {
        await outwardDocApi.updateOutwardDocumentation(entry.entry_id, updateData);
      }
      
      await outwardDocApi.confirmOutwardDocumentation(entry.entry_id);
      
      toast.success('Documentation confirmed successfully');
      fetchData();
    } catch (error) {
      console.error('Error confirming:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to confirm documentation';
      toast.error(errorMessage);
    }
  };

  const handleMoveToDraft = async () => {
    if (!moveToDraftEntry) return;
    
    setIsMovingToDraft(true);
    try {
      await apiClient.put(`/outward/entries/${moveToDraftEntry.entry_id}/move-to-draft`);
      toast.success('Entry moved to drafts');
      fetchData();
    } catch (error) {
      console.error('Error moving to draft:', error);
      toast.error('Failed to move entry to draft');
    } finally {
      setIsMovingToDraft(false);
      setMoveToDraftEntry(null);
    }
  };

  const getFieldValue = (entry, field) => {
    if (editingData[entry.entry_id]?.[field] !== undefined) {
      return editingData[entry.entry_id][field];
    }
    return entry.documentation?.[field] || '';
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Outward Entry Documentation</h1>
              <p className="text-sm text-[#E8DCC8]">AI-Powered Document Data Extraction</p>
            </div>
          </div>
          <Button onClick={fetchData} variant="secondary" className="h-10" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-full mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-lg font-semibold text-[#1A1F2E] font-display">
              Outward Document Extraction ({entries.length} entries)
            </h2>
            {/* 2026-06 — Warehouse filter (Phase 2) */}
            <Select
              value={warehouseFilter || 'all'}
              onValueChange={(v) => setWarehouseFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[200px] h-9 text-xs" data-testid="warehouse-filter-trigger">
                <SelectValue placeholder="All warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="warehouse-filter-all">All warehouses</SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w.warehouse_id} value={w.warehouse_id} data-testid={`warehouse-filter-${w.warehouse_id}`}>
                    {w.name}
                  </SelectItem>
                ))}
                <SelectItem value="__UNTAGGED__" data-testid="warehouse-filter-untagged">Untagged</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 2026-06 — FY warehouse bifurcation strip (Phase 2) */}
          {warehouseSummary.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="warehouse-breakdown">
              <span className="text-xs text-gray-400 uppercase tracking-wide">FY 26-27 by warehouse:</span>
              {warehouseSummary.map((w) => {
                const active = warehouseFilter === w.warehouse_id;
                return (
                  <button
                    key={w.warehouse_id}
                    type="button"
                    onClick={() => setWarehouseFilter(active ? '' : w.warehouse_id)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active ? 'bg-[#2D5F4F] text-white border-[#2D5F4F]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#2D5F4F]'
                    }`}
                    data-testid={`warehouse-chip-${w.warehouse_id}`}
                  >
                    {w.warehouse_name} <span className="opacity-70">({w.count})</span>
                  </button>
                );
              })}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F] mx-auto" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-[#5A5F6E]">
              No outward entries with images found
            </div>
          ) : (
            <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">SL No</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Document Barcode</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Gate Outward Date</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Gate Outward Time</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Staff ID</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Transfer Type</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Party Name *</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Doc No *</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Doc Date *</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Qty *</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Value</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Vehicle No</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase">Bundle Count</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-xs uppercase text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry, index) => {
                      const isExtracting = extractingIds.has(entry.entry_id);
                      const isConfirmed = entry.documentation?.extraction_status === 'confirmed';
                      const canEdit = !isConfirmed;
                      const { date, time } = formatDateTime(entry.timestamp);
                      
                      return (
                        <TableRow key={entry.entry_id} className="hover:bg-[#FFFBF5]">
                          <TableCell className="font-medium text-[#1A1F2E]">{index + 1}</TableCell>
                          <TableCell className="font-mono text-sm text-[#1A1F2E]">
                            <div className="flex items-center gap-2">
                              {entry.document_barcode}
                              <Button
                                onClick={() => handleViewImages(entry.entry_id)}
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2"
                                data-testid={`view-images-${entry.entry_id}`}
                              >
                                <ImageIcon className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-[#5A5F6E]">{date}</TableCell>
                          <TableCell className="text-sm text-[#5A5F6E]">{time}</TableCell>
                          <TableCell className="text-sm text-[#5A5F6E]">{entry.staff_id}</TableCell>
                          <TableCell className="text-sm text-[#5A5F6E]">
                            <span className={`px-2 py-1 rounded text-xs ${
                              entry.transfer_type === 'job_worker' ? 'bg-blue-100 text-blue-700' :
                              entry.transfer_type === 'internal_transfer' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {getTransferTypeLabel(entry.transfer_type)}
                            </span>
                          </TableCell>
                          <TableCell className="min-w-[180px]">
                            {isExtracting ? (
                              <Loader2 className="w-4 h-4 animate-spin text-[#2D5F4F]" />
                            ) : canEdit ? (
                              <Popover 
                                open={vendorPopoverOpen[entry.entry_id]} 
                                onOpenChange={(open) => setVendorPopoverOpen(prev => ({...prev, [entry.entry_id]: open}))}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    className="w-full h-8 justify-between text-sm font-normal truncate"
                                    data-testid={`vendor-select-${entry.entry_id}`}
                                  >
                                    <span className="truncate">
                                      {selectedVendors[entry.entry_id] || getFieldValue(entry, 'vendor_name') || 'Select/Enter party...'}
                                    </span>
                                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[280px] p-0" align="start">
                                  <Command>
                                    <CommandInput placeholder="Search or type party name..." />
                                    <CommandList>
                                      <CommandEmpty>
                                        <div className="p-2 text-sm text-gray-500">
                                          No match found. Type to add new.
                                        </div>
                                      </CommandEmpty>
                                      <CommandGroup heading="Recent Job Workers">
                                        {jobWorkers.map((worker) => (
                                          <CommandItem
                                            key={worker}
                                            value={worker}
                                            onSelect={() => handleVendorSelect(entry.entry_id, worker)}
                                            className="cursor-pointer"
                                          >
                                            {worker}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                  {getFieldValue(entry, 'vendor_name') && (
                                    <div className="border-t p-2 bg-gray-50">
                                      <p className="text-xs text-gray-500">AI Extracted:</p>
                                      <p className="text-xs font-medium text-blue-600 truncate">
                                        {getFieldValue(entry, 'vendor_name')}
                                      </p>
                                    </div>
                                  )}
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <div className="text-sm text-[#1A1F2E]">
                                {selectedVendors[entry.entry_id] || getFieldValue(entry, 'vendor_name') || '-'}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="min-w-[130px]">
                            {isExtracting ? (
                              <Loader2 className="w-4 h-4 animate-spin text-[#2D5F4F]" />
                            ) : (
                              <div className="text-sm text-[#1A1F2E]">
                                {getFieldValue(entry, 'vendor_doc_no') || '-'}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="min-w-[110px]">
                            {isExtracting ? (
                              <Loader2 className="w-4 h-4 animate-spin text-[#2D5F4F]" />
                            ) : (
                              <div className="text-sm text-[#1A1F2E]">
                                {formatVendorDate(getFieldValue(entry, 'vendor_doc_date')) || '-'}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="min-w-[100px]">
                            {isExtracting ? (
                              <Loader2 className="w-4 h-4 animate-spin text-[#2D5F4F]" />
                            ) : (
                              <div className="text-sm text-[#1A1F2E]">
                                {formatIndianNumber(getFieldValue(entry, 'qty')) || '-'}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="min-w-[120px]">
                            {isExtracting ? (
                              <Loader2 className="w-4 h-4 animate-spin text-[#2D5F4F]" />
                            ) : (
                              <div className="text-sm text-[#1A1F2E]">
                                {formatIndianNumber(getFieldValue(entry, 'value')) || '-'}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="min-w-[110px]">
                            {isExtracting ? (
                              <Loader2 className="w-4 h-4 animate-spin text-[#2D5F4F]" />
                            ) : (
                              <div className="text-sm text-[#1A1F2E]">
                                {getFieldValue(entry, 'vehicle_no') || '-'}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-[#5A5F6E]">{entry.bundle_count}</TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-2">
                              {canEdit && (
                                <>
                                  <Button
                                    onClick={() => autoExtract(entry.entry_id)}
                                    disabled={isExtracting}
                                    variant="outline"
                                    size="sm"
                                    className="h-8 border-blue-500 text-blue-500 hover:bg-blue-50"
                                    data-testid={`extract-${entry.entry_id}`}
                                  >
                                    {isExtracting ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <RotateCcw className="w-4 h-4 mr-1" />
                                        AI Extract
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    onClick={() => handleEditModal(entry)}
                                    variant="outline"
                                    size="sm"
                                    className="h-8"
                                    data-testid={`edit-${entry.entry_id}`}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    onClick={() => setMoveToDraftEntry(entry)}
                                    variant="outline"
                                    size="sm"
                                    className="h-8 border-orange-500 text-orange-500 hover:bg-orange-50"
                                    data-testid={`move-draft-${entry.entry_id}`}
                                  >
                                    <FileText className="w-4 h-4 mr-1" />
                                    Draft
                                  </Button>
                                  <Button
                                    onClick={() => handleConfirm(entry)}
                                    disabled={isExtracting}
                                    className="h-8 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                                    data-testid={`confirm-${entry.entry_id}`}
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Confirm
                                  </Button>
                                </>
                              )}
                              {isConfirmed && (
                                <span className="text-sm text-green-600 font-medium">✓ Confirmed</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      <Dialog open={editModalEntry !== null} onOpenChange={() => setEditModalEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Extracted Data - {editModalEntry?.document_barcode}</DialogTitle>
          </DialogHeader>
          {editModalEntry && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-medium">Party Name *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full mt-1 h-12 justify-between text-sm font-normal"
                    >
                      <span className="truncate">
                        {selectedVendors[editModalEntry.entry_id] || editingData[editModalEntry.entry_id]?.vendor_name || 'Select party...'}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search party..." />
                      <CommandList className="max-h-[300px]">
                        <CommandEmpty>No match found.</CommandEmpty>
                        <CommandGroup heading="Recent Job Workers">
                          {jobWorkers.map((worker) => (
                            <CommandItem
                              key={worker}
                              value={worker}
                              onSelect={() => {
                                handleVendorSelect(editModalEntry.entry_id, worker);
                              }}
                              className="cursor-pointer"
                            >
                              {worker}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {editingData[editModalEntry.entry_id]?.vendor_name && editingData[editModalEntry.entry_id]?.vendor_name !== selectedVendors[editModalEntry.entry_id] && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <p className="text-xs text-blue-600">AI Extracted: <span className="font-medium">{editingData[editModalEntry.entry_id]?.vendor_name}</span></p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Doc No *</Label>
                  <Textarea
                    value={editingData[editModalEntry.entry_id]?.vendor_doc_no || ''}
                    onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'vendor_doc_no', e.target.value)}
                    className="mt-1 min-h-[80px]"
                    placeholder="Enter document number"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Doc Date * (DD/MM/YYYY)</Label>
                  <Input
                    value={editingData[editModalEntry.entry_id]?.vendor_doc_date || ''}
                    onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'vendor_doc_date', e.target.value)}
                    className="mt-1 h-12"
                    placeholder="DD/MM/YYYY"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Qty (Quantity) *</Label>
                  <Input
                    value={editingData[editModalEntry.entry_id]?.qty || ''}
                    onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'qty', e.target.value)}
                    onBlur={(e) => {
                      const formatted = formatIndianNumber(e.target.value);
                      handleFieldChange(editModalEntry.entry_id, 'qty', formatted);
                    }}
                    className="mt-1 h-12 text-base"
                    placeholder="Enter quantity"
                  />
                  <p className="text-xs text-[#8A8F9E] mt-1">Auto-formats in Indian style (1,00,000)</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Value (Amount)</Label>
                  <Input
                    value={editingData[editModalEntry.entry_id]?.value || ''}
                    onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'value', e.target.value)}
                    onBlur={(e) => {
                      const formatted = formatIndianNumber(e.target.value);
                      handleFieldChange(editModalEntry.entry_id, 'value', formatted);
                    }}
                    className="mt-1 h-12 text-base"
                    placeholder="Enter value"
                  />
                  <p className="text-xs text-[#8A8F9E] mt-1">Auto-formats in Indian style (5,50,000.50)</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Vehicle No</Label>
                  <Input
                    value={editingData[editModalEntry.entry_id]?.vehicle_no || ''}
                    onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'vehicle_no', e.target.value)}
                    className="mt-1 h-12"
                    placeholder="Enter vehicle number"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Destination</Label>
                  <Input
                    value={editingData[editModalEntry.entry_id]?.destination || ''}
                    onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'destination', e.target.value)}
                    className="mt-1 h-12"
                    placeholder="Enter destination"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Remarks</Label>
                <Textarea
                  value={editingData[editModalEntry.entry_id]?.remarks || ''}
                  onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'remarks', e.target.value)}
                  className="mt-1 min-h-[80px]"
                  placeholder="Any additional notes"
                />
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => setEditModalEntry(null)}
                  variant="secondary"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveFromModal}
                  className="flex-1 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Viewer Dialogs */}
      <Dialog open={viewingImages !== null} onOpenChange={() => setViewingImages(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Document Images - Click to enlarge</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 max-h-[75vh] overflow-y-auto p-4">
            {viewingImages?.map((url, idx) => (
              <div
                key={idx}
                className="relative bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border-2 border-[#E5DFD0] hover:border-[#2D5F4F]"
                onClick={() => setEnlargedImage(url)}
                style={{ aspectRatio: '4/3' }}
              >
                <img
                  src={url}
                  alt={`Document ${idx + 1}`}
                  className="absolute inset-0 w-full h-full object-contain"
                />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={enlargedImage !== null} onOpenChange={() => setEnlargedImage(null)}>
        <DialogContent className="max-w-[98vw] max-h-[98vh] w-auto h-auto p-0 bg-black">
          <div className="relative flex items-center justify-center min-h-[80vh]">
            <img
              src={enlargedImage}
              alt="Enlarged document"
              className="max-w-full max-h-[95vh] object-contain"
              style={{ margin: 'auto' }}
            />
            <Button
              onClick={() => setEnlargedImage(null)}
              variant="ghost"
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-900"
            >
              ✕ Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move to Draft Confirmation */}
      <AlertDialog open={!!moveToDraftEntry} onOpenChange={() => setMoveToDraftEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move to Draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move entry "{moveToDraftEntry?.document_barcode}" back to draft status. 
              The entry will need to be re-submitted by the outward entry user.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMovingToDraft}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMoveToDraft}
              disabled={isMovingToDraft}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isMovingToDraft ? 'Moving...' : 'Move to Draft'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OutwardDocumentation;
